import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Divider,
  Pagination,
  Tooltip,
  Tab,
  Tabs,
  Stack,
  Badge,
  Avatar,
  LinearProgress,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Reply as ReplyIcon,
  BugReport as BugReportIcon,
  Lightbulb as LightbulbIcon,
  Help as HelpIcon,
  Build as BuildIcon,
  Payment as PaymentIcon,
  MoreHoriz as MoreIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  HourglassTop as HourglassTopIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  ArrowDropDown as ArrowDropDownIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Done as DoneIcon,
  Lock as LockIcon,
  FiberNew as NewIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface SupportTicket {
  ticket_id: string;
  business_id: number;
  business_name: string;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  ticket_type: 'bug' | 'feature_request' | 'general_inquiry' | 'technical_support' | 'billing' | 'other';
  status: 'open' | 'in_progress' | 'waiting_for_response' | 'resolved' | 'closed';
  user_name: string;
  user_email: string;
  assigned_to?: string;
  resolution_notes?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  closed_at?: string;
  response_count: number;
  last_response_at?: string;
}

interface TicketResponse {
  response_id: string;
  responder_email: string;
  responder_name: string;
  responder_type: 'user' | 'support' | 'system';
  message: string;
  is_internal: boolean;
  created_at: string;
}

interface Attachment {
  attachment_id: string;
  filename: string;
  file_url: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string;
  created_at: string;
  response_id?: string;
}

interface TicketDetails extends SupportTicket {
  responses: TicketResponse[];
  attachments: Attachment[];
}

interface StatusCounts {
  open: number;
  in_progress: number;
  waiting_for_response: number;
  resolved: number;
  closed: number;
}

interface Business {
  business_id: number;
  business_name: string;
}

const priorityColors: Record<string, string> = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444',
};

const statusColors: Record<string, string> = {
  open: '#6b7280',
  in_progress: '#f59e0b',
  waiting_for_response: '#8b5cf6',
  resolved: '#22c55e',
  closed: '#64748b',
};

const ticketTypeIcons: Record<string, React.ReactNode> = {
  bug: <BugReportIcon fontSize="small" />,
  feature_request: <LightbulbIcon fontSize="small" />,
  general_inquiry: <HelpIcon fontSize="small" />,
  technical_support: <BuildIcon fontSize="small" />,
  billing: <PaymentIcon fontSize="small" />,
  other: <MoreIcon fontSize="small" />,
};

const statusTabs = [
  { label: 'Open', value: 'open', icon: <NewIcon fontSize="small" /> },
  { label: 'In Progress', value: 'in_progress', icon: <PlayArrowIcon fontSize="small" /> },
  { label: 'Waiting', value: 'waiting_for_response', icon: <HourglassTopIcon fontSize="small" /> },
  { label: 'Resolved', value: 'resolved', icon: <CheckCircleIcon fontSize="small" /> },
  { label: 'Closed', value: 'closed', icon: <LockIcon fontSize="small" /> },
];

const statusTransitions = [
  { label: 'Open', value: 'open', icon: <NewIcon fontSize="small" />, color: '#6b7280' },
  { label: 'In Progress', value: 'in_progress', icon: <PlayArrowIcon fontSize="small" />, color: '#f59e0b' },
  { label: 'Waiting for Response', value: 'waiting_for_response', icon: <PauseIcon fontSize="small" />, color: '#8b5cf6' },
  { label: 'Resolved', value: 'resolved', icon: <DoneIcon fontSize="small" />, color: '#22c55e' },
  { label: 'Closed', value: 'closed', icon: <LockIcon fontSize="small" />, color: '#64748b' },
];

function formatDate(dateString: string) {
  const d = new Date(dateString);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mimeType: string) {
  return mimeType.startsWith('image/');
}

// ─── Attachment item ──────────────────────────────────────────────────────────
const AttachmentItem: React.FC<{ attachment: Attachment }> = ({ attachment }) => {
  if (isImage(attachment.mime_type)) {
    return (
      <Box
        component="a"
        href={attachment.file_url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: 'inline-block', mr: 1, mb: 1 }}
      >
        <Box
          component="img"
          src={attachment.file_url}
          alt={attachment.filename}
          sx={{
            maxWidth: 200,
            maxHeight: 150,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            objectFit: 'cover',
            cursor: 'pointer',
            '&:hover': { opacity: 0.85 },
          }}
        />
      </Box>
    );
  }
  return (
    <Chip
      icon={<FileIcon />}
      label={`${attachment.filename} (${formatFileSize(attachment.file_size)})`}
      component="a"
      href={attachment.file_url}
      target="_blank"
      rel="noopener noreferrer"
      clickable
      variant="outlined"
      size="small"
      sx={{ mr: 1, mb: 1 }}
    />
  );
};

// ─── Message bubble ───────────────────────────────────────────────────────────
const MessageBubble: React.FC<{ response: TicketResponse; attachments: Attachment[] }> = ({ response, attachments }) => {
  const isSupport = response.responder_type === 'support';
  const msgAttachments = attachments.filter(a => a.response_id === response.response_id);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSupport ? 'row' : 'row-reverse',
        alignItems: 'flex-start',
        gap: 1.5,
        mb: 2,
      }}
    >
      <Avatar
        sx={{
          width: 34,
          height: 34,
          bgcolor: isSupport ? 'primary.main' : 'grey.400',
          fontSize: '0.8rem',
          flexShrink: 0,
        }}
      >
        {isSupport ? 'S' : response.responder_name.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ maxWidth: '75%' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5, display: 'block', textAlign: isSupport ? 'left' : 'right' }}
        >
          {response.responder_name}
          {isSupport && (
            <Chip label="Support" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }} color="primary" />
          )}
          {' · '}{formatDate(response.created_at)}
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: isSupport ? 'primary.50' : 'grey.100',
            border: '1px solid',
            borderColor: isSupport ? 'primary.200' : 'grey.200',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {response.message}
          </Typography>
          {msgAttachments.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {msgAttachments.map(a => <AttachmentItem key={a.attachment_id} attachment={a} />)}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

// ─── Quick status chip with dropdown ─────────────────────────────────────────
const StatusChip: React.FC<{ ticket: SupportTicket; onStatusChange: (ticketId: string, status: string) => void }> = ({
  ticket,
  onStatusChange,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  return (
    <>
      <Chip
        label={ticket.status.replace(/_/g, ' ').toUpperCase()}
        size="small"
        deleteIcon={<ArrowDropDownIcon />}
        onDelete={e => { e.stopPropagation(); setAnchor(e.currentTarget as HTMLElement); }}
        onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ bgcolor: statusColors[ticket.status], color: 'white', cursor: 'pointer', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)' } }}
      />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {statusTransitions.map(s => (
          <MenuItem
            key={s.value}
            selected={s.value === ticket.status}
            onClick={() => { setAnchor(null); if (s.value !== ticket.status) onStatusChange(ticket.ticket_id, s.value); }}
          >
            <ListItemIcon sx={{ color: s.color }}>{s.icon}</ListItemIcon>
            <ListItemText>{s.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const SupportTicketManagement: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetails | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [statusCounts, setStatusCounts] = useState<StatusCounts>({ open: 0, in_progress: 0, waiting_for_response: 0, resolved: 0, closed: 0 });
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [filters, setFilters] = useState({ priority: 'all', business_id: 'all' });
  const [replyMessage, setReplyMessage] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [responseForm, setResponseForm] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    resolution_notes: '',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const [createForm, setCreateForm] = useState({
    subject: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    ticket_type: 'general_inquiry' as SupportTicket['ticket_type'],
  });
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createFileInputRef = useRef<HTMLInputElement>(null);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const currentStatus = statusTabs[tabValue].value;
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        status: currentStatus,
        priority: filters.priority,
        business_id: filters.business_id,
      });
      const res = await axios.get(`${API_BASE_URL}/support-tickets/admin/all?${params}`, authHeader());
      if (res.data.success) {
        setTickets(res.data.tickets);
        setTotalPages(res.data.pagination.pages);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, tabValue]);

  const fetchStatusCounts = useCallback(async () => {
    try {
      const params = filters.business_id !== 'all' ? `?business_id=${filters.business_id}` : '';
      const res = await axios.get(`${API_BASE_URL}/support-tickets/admin/counts${params}`, authHeader());
      if (res.data.success) setStatusCounts(res.data.counts);
    } catch {}
  }, [filters.business_id]);

  const fetchBusinesses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/businesses`, authHeader());
      if (res.data.success) {
        setBusinesses(res.data.businesses || res.data.data || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchStatusCounts();
  }, [currentPage, filters, tabValue, fetchTickets, fetchStatusCounts]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const openTicketDetail = async (ticketId: string) => {
    try {
      setLoadingDetail(true);
      setDetailOpen(true);
      const res = await axios.get(`${API_BASE_URL}/support-tickets/${ticketId}`, authHeader());
      if (res.data.success) {
        const t = res.data.ticket;
        setSelectedTicket(t);
        setResponseForm({
          status: t.status,
          priority: t.priority,
          assigned_to: t.assigned_to || '',
          resolution_notes: t.resolution_notes || '',
        });
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load ticket details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleQuickStatusChange = async (ticketId: string, status: string) => {
    try {
      await axios.put(`${API_BASE_URL}/support-tickets/${ticketId}`, { status }, authHeader());
      setSuccess(`Ticket moved to ${status.replace(/_/g, ' ')}`);
      fetchTickets();
      fetchStatusCounts();
      if (selectedTicket?.ticket_id === ticketId) {
        await openTicketDetail(ticketId);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update ticket status');
    }
  };

  const handleSendReply = async () => {
    if (!selectedTicket) return;

    try {
      setReplying(true);
      setError(null);

      const updateData: any = {};
      if (responseForm.status !== selectedTicket.status) updateData.status = responseForm.status;
      if (responseForm.priority !== selectedTicket.priority) updateData.priority = responseForm.priority;
      if (responseForm.assigned_to !== (selectedTicket.assigned_to || '')) updateData.assigned_to = responseForm.assigned_to;
      if (responseForm.resolution_notes !== (selectedTicket.resolution_notes || '')) updateData.resolution_notes = responseForm.resolution_notes;
      if (replyMessage.trim()) updateData.response_message = replyMessage.trim();

      const res = await axios.put(
        `${API_BASE_URL}/support-tickets/${selectedTicket.ticket_id}`,
        updateData,
        authHeader()
      );

      // Upload any pending attachments
      if (pendingFiles.length > 0 && res.data.success) {
        setUploadProgress(true);
        for (const file of pendingFiles) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(
            `${API_BASE_URL}/support-tickets/${selectedTicket.ticket_id}/attachments`,
            fd,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
        }
        setUploadProgress(false);
      }

      setSuccess('Ticket updated and reply sent!');
      setReplyMessage('');
      setPendingFiles([]);
      await openTicketDetail(selectedTicket.ticket_id);
      fetchTickets();
      fetchStatusCounts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update ticket');
    } finally {
      setReplying(false);
      setUploadProgress(false);
    }
  };

  const handleCreateSubmit = async () => {
    if (!createForm.subject.trim() || !createForm.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const res = await axios.post(`${API_BASE_URL}/support-tickets`, createForm, authHeader());
      if (res.data.success && createFiles.length > 0) {
        setUploadProgress(true);
        for (const file of createFiles) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(
            `${API_BASE_URL}/support-tickets/${res.data.ticketId}/attachments`,
            fd,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
        }
        setUploadProgress(false);
      }
      setSuccess('Support ticket created successfully.');
      setCreateDialogOpen(false);
      setCreateForm({ subject: '', description: '', priority: 'medium', ticket_type: 'general_inquiry' });
      setCreateFiles([]);
      fetchTickets();
      fetchStatusCounts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create support ticket');
    } finally {
      setSubmitting(false);
      setUploadProgress(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setPendingFiles(p => [...p, ...Array.from(e.target.files!)]);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedTicket(null);
    setReplyMessage('');
    setPendingFiles([]);
  };

  const tabCount = (status: string) => statusCounts[status as keyof StatusCounts] ?? 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Support Ticket Management</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<AddIcon />} variant="contained" color="primary" onClick={() => setCreateDialogOpen(true)}>
            Raise a Ticket
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={() => { fetchTickets(); fetchStatusCounts(); }} disabled={loading} variant="outlined">
            Refresh
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Status tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => { setTabValue(v); setCurrentPage(1); }}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {statusTabs.map((tab, i) => (
            <Tab
              key={tab.value}
              label={tab.label}
              icon={
                <Badge
                  badgeContent={tabCount(tab.value)}
                  color={tab.value === 'open' || tab.value === 'in_progress' ? 'error' : 'default'}
                  max={99}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', height: 16, minWidth: 16 } }}
                >
                  {tab.icon}
                </Badge>
              }
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={filters.priority} label="Priority" onChange={e => { setFilters(p => ({ ...p, priority: e.target.value })); setCurrentPage(1); }}>
              <MenuItem value="all">All Priority</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Business</InputLabel>
            <Select value={filters.business_id} label="Business" onChange={e => { setFilters(p => ({ ...p, business_id: e.target.value })); setCurrentPage(1); }}>
              <MenuItem value="all">All Businesses</MenuItem>
              {businesses.map(b => (
                <MenuItem key={b.business_id} value={String(b.business_id)}>{b.business_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
          </Typography>
        </Stack>
      </Paper>

      {/* Ticket table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                </TableRow>
              ) : tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <ScheduleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No tickets in this category</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map(ticket => (
                  <TableRow key={ticket.ticket_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BusinessIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.business_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ticket.subject}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        #{ticket.ticket_id.substring(0, 8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={ticket.ticket_type.replace(/_/g, ' ')}>
                        <Box>{ticketTypeIcons[ticket.ticket_type]}</Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.priority.toUpperCase()}
                        size="small"
                        sx={{ bgcolor: priorityColors[ticket.priority], color: 'white', fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip ticket={ticket} onStatusChange={handleQuickStatusChange} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{ticket.user_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{ticket.user_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatDate(ticket.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={ticket.assigned_to ? 'text.primary' : 'text.disabled'}>
                        {ticket.assigned_to || 'Unassigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View & Respond">
                        <IconButton size="small" color="primary" onClick={() => openTicketDetail(ticket.ticket_id)}>
                          <ReplyIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination count={totalPages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} color="primary" />
          </Box>
        )}
      </Paper>

      {/* ── Create Ticket Dialog ─────────────────────────────────────────── */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography variant="h6">Raise a Support Ticket</Typography>
          </Box>
          <IconButton onClick={() => setCreateDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Subject *"
              value={createForm.subject}
              onChange={e => setCreateForm(p => ({ ...p, subject: e.target.value }))}
              fullWidth
              size="small"
              placeholder="Brief summary of the issue"
            />
            <Stack direction="row" spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={createForm.ticket_type} label="Type" onChange={e => setCreateForm(p => ({ ...p, ticket_type: e.target.value as typeof createForm.ticket_type }))}>
                  <MenuItem value="bug">Bug Report</MenuItem>
                  <MenuItem value="feature_request">Feature Request</MenuItem>
                  <MenuItem value="general_inquiry">General Inquiry</MenuItem>
                  <MenuItem value="technical_support">Technical Support</MenuItem>
                  <MenuItem value="billing">Billing</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={createForm.priority} label="Priority" onChange={e => setCreateForm(p => ({ ...p, priority: e.target.value as typeof createForm.priority }))}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label="Description *"
              value={createForm.description}
              onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
              fullWidth
              multiline
              rows={5}
              placeholder="Please describe your issue in detail…"
            />
            {createFiles.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {createFiles.map((f, i) => (
                  <Chip
                    key={i}
                    label={f.name}
                    icon={isImage(f.type) ? <ImageIcon /> : <FileIcon />}
                    onDelete={() => setCreateFiles(p => p.filter((_, idx) => idx !== i))}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
            {uploadProgress && <LinearProgress />}
            <input
              ref={createFileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files) setCreateFiles(p => [...p, ...Array.from(e.target.files!)]); }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => createFileInputRef.current?.click()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Attach Files
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={submitting || !createForm.subject.trim() || !createForm.description.trim()}
            startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {submitting ? 'Submitting…' : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Ticket Detail / Respond Dialog ───────────────────────────────── */}
      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReplyIcon color="primary" />
            <Typography variant="h6">
              {selectedTicket ? selectedTicket.subject : 'Loading…'}
            </Typography>
          </Box>
          <IconButton onClick={closeDetail}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {loadingDetail || !selectedTicket ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ display: 'flex', height: '75vh' }}>
              {/* Left: conversation */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid', borderColor: 'divider' }}>
                {/* Ticket meta */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={1}>
                    <Chip label={selectedTicket.priority.toUpperCase()} size="small" sx={{ bgcolor: priorityColors[selectedTicket.priority], color: 'white', fontWeight: 700 }} />
                    <Chip label={selectedTicket.status.replace(/_/g, ' ').toUpperCase()} size="small" sx={{ bgcolor: statusColors[selectedTicket.status], color: 'white' }} />
                    <Chip label={selectedTicket.ticket_type.replace(/_/g, ' ')} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    <strong>{selectedTicket.business_name}</strong> · {selectedTicket.user_name} ({selectedTicket.user_email}) · {formatDate(selectedTicket.created_at)}
                  </Typography>
                  {selectedTicket.assigned_to && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Assigned to: <strong>{selectedTicket.assigned_to}</strong>
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Original Request
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {selectedTicket.description}
                  </Typography>
                  {selectedTicket.attachments.filter(a => !a.response_id).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {selectedTicket.attachments.filter(a => !a.response_id).map(a => (
                        <AttachmentItem key={a.attachment_id} attachment={a} />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Chat thread */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                  {selectedTicket.responses.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <ReplyIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">No responses yet</Typography>
                    </Box>
                  ) : (
                    selectedTicket.responses.map(r => (
                      <MessageBubble key={r.response_id} response={r} attachments={selectedTicket.attachments} />
                    ))
                  )}
                  <div ref={chatEndRef} />
                </Box>

                {/* Reply box */}
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  {pendingFiles.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      {pendingFiles.map((f, i) => (
                        <Chip
                          key={i}
                          label={f.name}
                          icon={isImage(f.type) ? <ImageIcon /> : <FileIcon />}
                          onDelete={() => setPendingFiles(p => p.filter((_, idx) => idx !== i))}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                  {uploadProgress && <LinearProgress sx={{ mb: 1 }} />}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                      placeholder="Type a response to send to the user… (Ctrl+Enter to send)"
                      value={replyMessage}
                      onChange={e => setReplyMessage(e.target.value)}
                      multiline
                      maxRows={5}
                      fullWidth
                      size="small"
                      onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSendReply(); }}
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                    <Tooltip title="Attach image or file">
                      <IconButton onClick={() => fileInputRef.current?.click()}>
                        <AttachFileIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Send update">
                      <span>
                        <IconButton
                          color="primary"
                          onClick={handleSendReply}
                          disabled={replying || (!replyMessage.trim() && pendingFiles.length === 0 && responseForm.status === selectedTicket.status && responseForm.priority === selectedTicket.priority && responseForm.assigned_to === (selectedTicket.assigned_to || '') && responseForm.resolution_notes === (selectedTicket.resolution_notes || ''))}
                        >
                          {replying ? <CircularProgress size={20} /> : <SendIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              {/* Right: ticket management panel */}
              <Box sx={{ width: 300, p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                  Ticket Controls
                </Typography>

                {/* Quick status buttons */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Move to Status</Typography>
                  <Stack spacing={1}>
                    {statusTransitions.map(s => (
                      <Button
                        key={s.value}
                        size="small"
                        variant={responseForm.status === s.value ? 'contained' : 'outlined'}
                        startIcon={s.icon}
                        onClick={() => setResponseForm(p => ({ ...p, status: s.value }))}
                        sx={{
                          justifyContent: 'flex-start',
                          borderColor: s.color,
                          color: responseForm.status === s.value ? 'white' : s.color,
                          bgcolor: responseForm.status === s.value ? s.color : 'transparent',
                          '&:hover': { bgcolor: s.color, color: 'white' },
                        }}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>

                <Divider />

                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select value={responseForm.priority} label="Priority" onChange={e => setResponseForm(p => ({ ...p, priority: e.target.value }))}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Assign To"
                  value={responseForm.assigned_to}
                  onChange={e => setResponseForm(p => ({ ...p, assigned_to: e.target.value }))}
                  size="small"
                  fullWidth
                  placeholder="Name or email"
                />

                <TextField
                  label="Resolution Notes"
                  value={responseForm.resolution_notes}
                  onChange={e => setResponseForm(p => ({ ...p, resolution_notes: e.target.value }))}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Internal notes (not sent to user)"
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSendReply}
                  disabled={replying}
                  startIcon={replying ? <CircularProgress size={16} /> : <SendIcon />}
                >
                  {replying ? 'Sending…' : 'Send Update'}
                </Button>

                {selectedTicket.resolution_notes && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Current Resolution Notes</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{selectedTicket.resolution_notes}</Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupportTicketManagement;
